import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ChevronUp, ChevronDown, Pencil, Trash2, Plus, CheckCircle2 } from "lucide-react";
import { useCategories, useCreateCategory, useUpdateCategory, useDeleteCategory, type Category } from "@/hooks/useCategories";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface CategoryManagementSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemCountsByCategory: Record<string, number>;
}

export function CategoryManagementSheet({ open, onOpenChange, itemCountsByCategory }: CategoryManagementSheetProps) {
  const { data: categories } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const startAdd = () => {
    setEditingId(null);
    setFormName("");
    setFormDescription("");
    setIsAdding(true);
  };

  const startEdit = (cat: Category) => {
    setIsAdding(false);
    setEditingId(cat.id);
    setFormName(cat.name);
    setFormDescription(cat.description ?? "");
  };

  const cancelForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormName("");
    setFormDescription("");
  };

  const handleSave = () => {
    if (!formName.trim()) return;
    if (isAdding) {
      const maxSort = categories?.reduce((max, c) => Math.max(max, c.sort_order), 0) ?? 0;
      createCategory.mutate(
        { name: formName.trim(), description: formDescription.trim() || undefined, sort_order: maxSort + 1 },
        { onSuccess: cancelForm }
      );
    } else if (editingId) {
      updateCategory.mutate(
        { id: editingId, name: formName.trim(), description: formDescription.trim() || undefined },
        { onSuccess: cancelForm }
      );
    }
  };

  const moveCategory = (cat: Category, direction: "up" | "down") => {
    if (!categories) return;
    const sorted = [...categories].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((c) => c.id === cat.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    updateCategory.mutate({ id: cat.id, sort_order: other.sort_order });
    updateCategory.mutate({ id: other.id, sort_order: cat.sort_order });
  };

  const sorted = categories ? [...categories].sort((a, b) => a.sort_order - b.sort_order) : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Manage Categories</SheetTitle>
          <SheetDescription>
            Organize your menu into categories. {sorted.length} categories total.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Add/Edit form */}
          {(isAdding || editingId) && (
            <div className="space-y-3 rounded-lg border border-primary/20 bg-muted/30 p-4">
              <p className="text-sm font-medium">{isAdding ? "New Category" : "Edit Category"}</p>
              <div className="space-y-2">
                <Label className="text-xs">Name</Label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Appetizers"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Description (optional)</Label>
                <Input
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Brief description..."
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={!formName.trim() || createCategory.isPending || updateCategory.isPending}>
                  {isAdding ? "Add" : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelForm}>Cancel</Button>
              </div>
            </div>
          )}

          {!isAdding && !editingId && (
            <Button variant="outline" size="sm" className="w-full" onClick={startAdd}>
              <Plus className="h-4 w-4 mr-2" /> Add Category
            </Button>
          )}

          <Separator />

          {/* Category list */}
          <div className="space-y-1">
            {sorted.map((cat, idx) => {
              const count = itemCountsByCategory[cat.id] ?? 0;
              const isEditing = editingId === cat.id;

              return (
                <div
                  key={cat.id}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 group transition-colors ${isEditing ? "bg-accent" : "hover:bg-muted/50"}`}
                >
                  {/* Reorder buttons */}
                  <div className="flex flex-col -my-1">
                    <button
                      onClick={() => moveCategory(cat, "up")}
                      disabled={idx === 0}
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => moveCategory(cat, "down")}
                      disabled={idx === sorted.length - 1}
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{cat.name}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                        {count}
                      </Badge>
                      {cat.square_catalog_id && (
                        <span title="Synced to Square">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                        </span>
                      )}
                    </div>
                    {cat.description && (
                      <p className="text-xs text-muted-foreground truncate">{cat.description}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <Switch
                    checked={cat.is_active}
                    onCheckedChange={(checked) => updateCategory.mutate({ id: cat.id, is_active: checked })}
                    className="shrink-0"
                  />
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100" onClick={() => startEdit(cat)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{cat.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {count > 0
                            ? `This will also remove ${count} menu item${count > 1 ? "s" : ""} in this category. This action cannot be undone.`
                            : "This action cannot be undone."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteCategory.mutate(cat.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })}

            {sorted.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No categories yet. Add one to start organizing your menu.
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
